import { ShieldOff } from 'lucide-react';
import { Box, Button, Heading, Stack, Text } from 'pixelize-design-library';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppTheme } from '@core/Theme/useAppTheme';
import { canvas, mutedText, statusInk, surface } from '@core/Theme/tokens';
import { homePathForRole } from '@core/utils/roleHome';
import { useAppSelector } from '@core/store/hooks';

/**
 * Rendered IN PLACE by a guard — never reached by `<Navigate>`. Redirecting away from a
 * forbidden route loses the URL the user actually asked for, so they cannot tell a typo
 * from a missing permission, and a bookmark silently becomes a dashboard link.
 */
export function PermissionDeniedPage() {
  const navigate = useNavigate();
  const { colors } = useAppTheme();
  const { t } = useTranslation();
  const role = useAppSelector((s) => s.auth.user?.role);

  return (
    <Box
      minH='60vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
      bg={canvas(colors)}
      p={6}
    >
      <Stack
        spacing={4}
        align='center'
        textAlign='center'
        maxW='md'
        bg={surface(colors)}
        borderRadius='xl'
        borderWidth='1px'
        borderColor={colors.boxborder[200]}
        p={8}
        data-testid='permission-denied-page'
      >
        <ShieldOff size={40} color={statusInk(colors, 'warning')} />
        <Heading size='md' color={colors.header[900]}>
          {t('errorPage.deniedTitle')}
        </Heading>
        <Text fontSize='sm' color={mutedText(colors)}>
          {t('errorPage.deniedBody')}
        </Text>
        <Button
          label={t('errorPage.backToDashboard')}
          colorScheme='primary'
          onClick={() => navigate(homePathForRole(role))}
          data-testid='permission-denied-home'
        />
      </Stack>
    </Box>
  );
}
